import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading';

interface Attachment {
    docName: string;
    fileName: string;
    famCodeDocID: string;
    url?: string;
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const EL_AttachFileSupport: React.FC = () => {
    const [searchParams] = useSearchParams();
    
    const [docName, setDocName] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadedDocs, setUploadedDocs] = useState<Attachment[]>([]);
    const [deleteList, setDeleteList] = useState<number[]>([]); 
    const [loading, setLoading] = useState(false);
    const [uploadId, setUploadId] = useState<string>('1');
    const [isReadOnly, setIsReadOnly] = useState(false);

    useEffect(() => {
        const paramNames = searchParams.get('FileName') || '';
        const paramFiles = searchParams.get('DocumentName') || '';
        const paramUrls = searchParams.get('DocumentUrl') || '';
        const docOpenOnly = searchParams.get('DocOpenOnly') || '';
        if (docOpenOnly === 'Y') setIsReadOnly(true);

        if (paramNames && paramFiles) {
            const names = paramNames.split('|');
            const files = paramFiles.split('|');
            const urls = paramUrls.split('|');

            const loaded: Attachment[] = [];
            
            names.forEach((name, index) => {
                if (files[index]) {
                    loaded.push({
                        docName: name,
                        fileName: files[index],
                        famCodeDocID: '0',
                        url: urls[index] || undefined
                    });
                }
            });

            setUploadedDocs(loaded);
        }
    }, [searchParams]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!docName) {
            alert("Enter the name.");
            return;
        }

        if (docName.includes('|') || docName.includes('*')) {
            alert("Document name cannot contain | or *");
            return;
        }

        if (!selectedFile) {
            alert("Invalid Document");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        const fromPage = searchParams.get('FromPage') || '';
        formData.append('fromPage', fromPage);

        try {
            const response = await fetch(`${API_BASE_URL}api/upload_support_file/`, {
                method: 'POST',
                body: formData,
            });
            
            const result = await response.json();
            if (!response.ok) throw new Error(result.error);

            setUploadedDocs([...uploadedDocs, { 
                docName: docName, 
                fileName: result.filename,
                famCodeDocID: '0',
                url: result.url
            }]);

            if (result.uploadId) setUploadId(result.uploadId);
            setDocName('');
            setSelectedFile(null);
            
            const fileInput = document.getElementById('fileInput') as HTMLInputElement;
            if(fileInput) fileInput.value = "";

        } catch (err: any) {
            alert("Upload failed: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (deleteList.length === 0) {
            alert("Please select attachment(s) to delete.");
            return;
        }
        if (!window.confirm("Do you really want to delete the selected attachment(s)?")) return;

        setLoading(true);

        const filesToDelete = uploadedDocs.filter((_, index) => deleteList.includes(index));
        const fromPage = searchParams.get('FromPage') || '';

        try {
            await Promise.all(filesToDelete.map(doc => 
                fetch(`${API_BASE_URL}api/delete_support_file/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ fileName: doc.fileName, fromPage: fromPage })
                })
            ));

            const newDocs = uploadedDocs.filter((_, index) => !deleteList.includes(index));
            setUploadedDocs(newDocs);
            setDeleteList([]);

        } catch (err: any) {
            console.error("Delete Error:", err);
            alert("Error deleting file from server: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckbox = (index: number) => {
        if (deleteList.includes(index)) {
            setDeleteList(deleteList.filter(i => i !== index));
        } else {
            setDeleteList([...deleteList, index]);
        }
    };

    const handleClose = () => {
        if (window.opener && !window.opener.closed) {
            const docNamesStr = uploadedDocs.map(d => d.docName).join('|');
            const fileNamesStr = uploadedDocs.map(d => d.fileName).join('|');
            const retString = uploadedDocs.length > 0 
                ? `${docNamesStr}*${fileNamesStr}*${uploadId}` 
                : null;
            
            window.opener.returnValue = retString;
            window.opener.ret = retString;

            if ((window.opener as any).handleAttachments) {
                const attachmentSession = Number(searchParams.get('AttachmentSession') || 0);
                (window.opener as any).handleAttachments(uploadedDocs, attachmentSession);
            }
        }
        window.close();
    };

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100vh',
            fontFamily: 'Trebuchet MS', 
            fontSize: '10pt', 
            backgroundColor: '#f8f8f8', 
            border: '1px solid #000080', 
            padding: '10px',
            boxSizing: 'border-box',
        }}>
            {loading && <Loading />}
            <div style={{ flexShrink: 0 }}>
                <table style={{ width: '100%' }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '80px', fontWeight: 'bold' }}>Name:</td>
                            <td>
                                <input 
                                    type="text" 
                                    value={docName} 
                                    onChange={(e) => setDocName(e.target.value)} 
                                    maxLength={10}
                                    disabled={isReadOnly}
                                    style={{ width: '100%', border: '2px ridge black', padding: '2px' }}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 'bold' }}>Location:</td>
                            <td>
                                <input 
                                    id="fileInput"
                                    type="file" 
                                    onChange={handleFileChange}
                                    disabled={isReadOnly}
                                    style={{ width: '100%', border: '2px ridge black', backgroundColor: 'white' }}
                                />
                            </td>
                        </tr>
                        <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>
                        <tr>
                            <td colSpan={2} style={{ textAlign: 'center' }}>
                                {!isReadOnly && <button onClick={handleUpload} style={styles.button}>Save</button>}
                                {!isReadOnly && <button onClick={handleDelete} style={styles.button}>Delete</button>}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{ 
                flex: 1,
                overflowY: 'auto', 
                border: '1px solid #b0b0b0', 
                backgroundColor: 'white', 
                marginTop: '15px', 
                marginBottom: '10px',
                padding: '0' 
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#e0e0e0', color: 'black', position: 'sticky', top: 0 }}>
                        <tr>
                            <td style={{ padding: '5px', fontWeight: 'bold', borderBottom: '1px solid #b0b0b0' }}>Document</td>
                            <td style={{ width: '40px', textAlign: 'center', borderBottom: '1px solid #b0b0b0' }}></td>
                        </tr>
                    </thead>
                    <tbody>
                        {uploadedDocs.length > 0 ? uploadedDocs.map((doc, i) => (
                            <tr key={i}>
                                <td style={{ borderBottom: '1px solid #e2e2e2', padding: '5px' }}>
                                    <a
                                        href={doc.url || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'blue', textDecoration: 'none' }}
                                        onClick={(e) => {
                                            if (!doc.url) e.preventDefault();
                                        }}
                                    >
                                        {doc.docName}
                                    </a>
                                </td>
                                <td style={{ borderBottom: '1px solid #e2e2e2', textAlign: 'center' }}>
                                    {!isReadOnly && (
                                        <input
                                            type="checkbox"
                                            checked={deleteList.includes(i)}
                                            onChange={() => handleCheckbox(i)}
                                        />
                                    )}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={2} style={{ padding: '10px', color: '#888', fontStyle: 'italic' }}>
                                    No documents attached.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div style={{ flexShrink: 0, textAlign: 'right' }}>
                <button onClick={handleClose} style={styles.button}>Close</button>
            </div>
        </div>
    );
};

const styles = {
    button: {
        backgroundColor: '#e0e0e0',
        border: '1px solid #808080',
        padding: '3px 10px',
        cursor: 'pointer',
        fontWeight: 'bold',
        minWidth: '70px',
        margin: '0 5px',
        fontFamily: 'Trebuchet MS',
        fontSize: '10pt'
    }
};

export default EL_AttachFileSupport;
